/**
 * Apple UGC Guideline 1.2 compliance — Terms/EULA gate.
 *
 * Behaviour (post checkbox-removal, Feb 2026):
 *   • No checkbox — acknowledgment text sits BELOW the primary action button.
 *   • First time a user clicks the sign-in / send-code button, the Terms modal
 *     opens automatically. Accepting the modal proceeds with the pending auth
 *     action. Cancelling aborts silently.
 *   • Acceptance is scoped to the CURRENT SESSION only (in-memory). Rationale:
 *     at sign-in we don't yet know which user is signing in, so we cannot check
 *     a per-user server flag. Persisting locally means a second user on the
 *     same device/browser would never see the consent. In-memory means each
 *     fresh sign-in attempt after a reload shows the consent again — safe.
 *   • Server-side per-user audit trail is written via
 *     POST /api/safety/guidelines/accept after acceptance.
 *
 * Usage:
 *   const { ensureAccepted, TermsFooter, TermsModalUi, openTerms } = useTermsGate();
 *   const onSubmit = () => ensureAccepted(() => doSubmit());
 *   return (<> <button ... /> {TermsFooter} {TermsModalUi} </>);
 */
import { useCallback, useRef, useState } from 'react';
import { ShieldCheck, ExternalLink } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

export const TERMS_URL = 'https://bowerschool.com/terms-of-service';
export const PRIVACY_URL = 'https://bowerschool.com/privacy-policy';

async function persistOnServer() {
  try {
    const base = process.env.REACT_APP_BACKEND_URL;
    await fetch(`${base}/api/safety/guidelines/accept`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ version: 'v1' }),
      credentials: 'include',
    });
  } catch (_) { /* silent — local persistence is authoritative for UX */ }
}

function TermsModal({ open, onAccept, onClose }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg max-h-[80vh] flex flex-col p-0" data-testid="terms-modal">
        <DialogHeader className="p-5 border-b border-border">
          <DialogTitle className="flex items-center gap-2 font-display">
            <ShieldCheck className="h-5 w-5 text-primary" />
            Community Rules & Terms
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto p-5 text-sm space-y-3">
          <p className="text-muted-foreground">
            Please read and accept before signing in. These apply to every founder, mentor, and staff member on Bower.
          </p>
          <div>
            <h4 className="font-semibold mb-1">Zero tolerance for objectionable content and abusive behaviour</h4>
            <p className="text-muted-foreground">
              Bower is a community for founders, students, and mentors. We have{' '}
              <span className="font-semibold text-foreground">zero tolerance</span> for objectionable content,
              harassment, hate speech, bullying, sexual or violent material, spam, doxxing, threats, fraud,
              illegal activity, impersonation, or any content or behaviour that targets or endangers another
              member. Accounts that violate these rules will be removed. Serious violations may be reported to
              law enforcement.
            </p>
          </div>
          <div>
            <h4 className="font-semibold mb-1">Your responsibilities</h4>
            <p className="text-muted-foreground">
              Keep content respectful, share only what you have the right to share, respect the privacy of
              other members, and use the platform for legitimate learning, building, and community purposes
              only.
            </p>
          </div>
          <div>
            <h4 className="font-semibold mb-1">Reporting & blocking</h4>
            <p className="text-muted-foreground">
              You can flag any post, comment, or profile for review. You can block any member; blocked users
              cannot see or interact with your content. We review every report within 24 hours and remove
              content that violates these rules.
            </p>
          </div>
          <div className="pt-2 space-y-1">
            <a href={TERMS_URL} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1 text-primary text-sm"
               data-testid="terms-modal-terms-link">
              Read full Terms of Service <ExternalLink className="h-3 w-3" />
            </a>
            <br />
            <a href={PRIVACY_URL} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1 text-primary text-sm"
               data-testid="terms-modal-privacy-link">
              Read Privacy Policy <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
        <div className="p-5 border-t border-border flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
          <Button variant="ghost" onClick={onClose} data-testid="terms-decline">Cancel</Button>
          <Button onClick={onAccept} data-testid="terms-accept">I agree — continue</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function useTermsGate() {
  const [accepted, setAccepted] = useState(false);
  const [open, setOpen] = useState(false);
  const pending = useRef(null);

  const acceptNow = useCallback(async () => {
    setAccepted(true);
    setOpen(false);
    persistOnServer();
    if (pending.current) {
      const fn = pending.current;
      pending.current = null;
      fn();
    }
  }, []);

  const ensureAccepted = useCallback((action) => {
    if (accepted) return action();
    pending.current = action;
    setOpen(true);
  }, [accepted]);

  const openTerms = useCallback(() => setOpen(true), []);

  // Standardised acknowledgment text — placed BELOW the primary action button.
  // Identical wording to the mobile app for cross-platform consistency.
  const TermsFooter = (
    <p
      className="mt-3 text-[11px] leading-relaxed text-muted-foreground text-center"
      data-testid="signin-terms-footer"
    >
      By continuing, you agree to Bower&apos;s{' '}
      <a href={TERMS_URL} target="_blank" rel="noreferrer"
         className="text-primary hover:underline font-medium"
         data-testid="signin-terms-link">
        Terms of Service
      </a>{' '}and{' '}
      <a href={PRIVACY_URL} target="_blank" rel="noreferrer"
         className="text-primary hover:underline font-medium"
         data-testid="signin-privacy-link">
        Privacy Policy
      </a>, and acknowledge that Bower has{' '}
      <span className="font-semibold text-foreground">zero tolerance for objectionable content or abusive behaviour</span>.
    </p>
  );

  const TermsModalUi = (
    <TermsModal
      open={open}
      onAccept={acceptNow}
      onClose={() => { pending.current = null; setOpen(false); }}
    />
  );

  return { accepted, ensureAccepted, TermsFooter, TermsModalUi, openTerms };
}

export default useTermsGate;
